import { useState } from "react";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

export type SocialUploadedFile = {
    id: string;
    name: string;
    url: string;
    mimeType: string;
    size?: number;
    kind: "image" | "document";
};

const CMS_MEDIA_URL = "/api/cms/contents/medias";

const resolveFileKind = (file: File, mimeType: string): SocialUploadedFile["kind"] => {
    if (file.type.startsWith("image/") || mimeType.startsWith("image/")) {
        return "image";
    }

    return "document";
};

export const useSocialFileUpload = () => {
    const [uploading, setUploading] = useState(false);

    const uploadSingle = async (file: File): Promise<SocialUploadedFile> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("name", file.name);

        const response = await loggedInApiCall.post(CMS_MEDIA_URL, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

        const mimeType = response.data.mimeType ?? file.type;

        return {
            id: response.data.id,
            name: response.data.name,
            url: response.data.url,
            mimeType,
            size: response.data.size,
            kind: resolveFileKind(file, mimeType),
        };
    };

    const uploadFiles = async (files: File[]): Promise<SocialUploadedFile[]> => {
        if (files.length === 0) {
            return [];
        }

        setUploading(true);

        try {
            const uploaded: SocialUploadedFile[] = [];

            for (const file of files) {
                uploaded.push(await uploadSingle(file));
            }

            return uploaded;
        } finally {
            setUploading(false);
        }
    };

    return {
        uploadFiles,
        uploading,
    };
};

export const splitUploadedFiles = (files: SocialUploadedFile[]) => ({
    images: files.filter((file) => file.kind === "image"),
    documents: files.filter((file) => file.kind === "document"),
});

export const toAttachmentMetadata = (files: SocialUploadedFile[]) => {
    const { images, documents } = splitUploadedFiles(files);

    return {
        images: images.map(({ id, name, url, mimeType, size }) => ({
            id,
            name,
            url,
            mimeType,
            size,
        })),
        documents: documents.map(({ id, name, url, mimeType, size }) => ({
            id,
            name,
            url,
            mimeType,
            size,
        })),
    };
};
